/**
 * DisruptionBox - Kompakte Störungsanzeige (Legacy-Style)
 * 
 * Business-App Design:
 * - Kompakt und nicht-invasiv
 * - Button-basierte Auswahl (kein Freitext)
 * - Hauptfokus bleibt auf Leistungserfassung
 */

import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Plus,
  Clock,
  CheckCircle,
  MapPin,
  X,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { ModuleInlineBadge } from './ModuleInlineBadge';
import { getTodayBerlin } from '@wash-ops/shared';

interface Disruption {
  id: string;
  categoryNameSnapshot?: string | null;
  category?: { name: string } | null;
  location: string | null;
  startedAt: string;
  endedAt: string | null;
  recordedBy?: { id: string; displayName: string };
  machine?: { name: string } | null;
}

interface DisruptionCategory {
  id: string;
  code: string;
  name: string;
  color?: string | null;
}

interface Machine {
  id: string;
  name: string;
  type?: string;
}

interface Line {
  id: string;
  name: string;
  subLines?: { id: string; name: string }[];
}

interface DisruptionBoxProps {
  stationId?: string;
  stationType?: 'WSA' | 'VORARBEITER';
  isAdminMode?: boolean;  // Admin darf alle Störungen sehen (inkl. ADMIN_ONLY)
}

export function DisruptionBox({ stationId, stationType, isAdminMode = false }: DisruptionBoxProps) {
  const [activeDisruptions, setActiveDisruptions] = useState<Disruption[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  // Locations für Start-Dialog
  const [machines, setMachines] = useState<Machine[]>([]);
  const [lines, setLines] = useState<Line[]>([]);

  // Dialog States
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedMachineId, setSelectedMachineId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedParentLine, setSelectedParentLine] = useState<Line | null>(null);
  const [disruptionToResolve, setDisruptionToResolve] = useState<Disruption | null>(null);

  // Nachtrag (Meldung #19): vergangene Störung mit Von–Bis erfassen
  const [nachtragModus, setNachtragModus] = useState(false);
  const [nachtragTag, setNachtragTag] = useState<'heute' | 'gestern'>('heute');
  const [nachtragVon, setNachtragVon] = useState('');
  const [nachtragBis, setNachtragBis] = useState('');

  // Störungs-Kategorien (dynamisch geladen)
  const [categories, setCategories] = useState<DisruptionCategory[]>([]);

  // Locations die bereits gestoert sind
  const disruptedLocations = activeDisruptions.map(d => d.location).filter(Boolean) as string[];

  // Locations laden - strikt nach contextType
  const loadLocations = useCallback(async () => {
    try {
      if (stationType === 'VORARBEITER') {
        const res = await fetch('/api/data/lines', { credentials: 'include' });
        if (res.ok) {
          setLines(await res.json());
        }
        setMachines([]);
      } else if (stationType === 'WSA') {
        const res = await fetch('/api/data/machines', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setMachines(data.filter((m: Machine) => m.type === 'WSA' || !m.type));
        }
        setLines([]);
      }
    } catch {
      console.error('Fehler beim Laden der Locations');
    }
  }, [stationType]);

  const loadActiveDisruptions = useCallback(async () => {
    try {
      // Endpoint basierend auf Modus:
      // - Worker-Mode (mit stationId): nur Störungen dieser Station (visibility=ALL)
      // - Admin-Mode (ohne stationId): alle aktiven Störungen (inkl. ADMIN_ONLY)
      // WICHTIG: Worker ohne stationId sehen KEINE Störungen (fail-closed)
      let url: string;
      if (stationId) {
        // Worker/Station-Mode: nur diese Station, nur visibility=ALL
        url = `/api/disruptions/active/for-station?stationId=${stationId}`;
      } else if (isAdminMode) {
        // Admin-Mode ohne Station: alle aktiven (inkl. ADMIN_ONLY)
        url = `/api/disruptions/active`;
      } else {
        // Worker ohne stationId: fail-closed, keine Störungen laden
        setActiveDisruptions([]);
        setLoading(false);
        return;
      }
      
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Alle aktiven Störungen (ohne endedAt)
        const active = data.filter((d: Disruption) => !d.endedAt);
        setActiveDisruptions(active);
      }
    } catch {
      console.error('Fehler beim Laden der Störungen');
    } finally {
      setLoading(false);
    }
  }, [stationId]);

  // Kategorien laden
  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch('/api/disruptions/categories', { credentials: 'include' });
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch {
      console.error('Fehler beim Laden der Kategorien');
    }
  }, []);

  useEffect(() => {
    loadActiveDisruptions();
    loadLocations();
    loadCategories();
    const interval = setInterval(loadActiveDisruptions, 30000);
    return () => clearInterval(interval);
  }, [loadActiveDisruptions, loadLocations, loadCategories]);

  const schliesseStartDialog = () => {
    setShowStartDialog(false);
    setSelectedLocation('');
    setSelectedMachineId(null);
    setSelectedCategoryId('');
    setSelectedParentLine(null);
    setNachtragModus(false);
    setNachtragTag('heute');
    setNachtragVon('');
    setNachtragBis('');
    setError('');
  };

  const handleStart = async () => {
    // Client-side Validation
    if (!selectedLocation) {
      setError('Bitte Ort/Maschine auswählen');
      return;
    }
    if (nachtragModus && (!nachtragVon || !nachtragBis)) {
      setError('Bitte Von- und Bis-Uhrzeit angeben');
      return;
    }
    // categoryId ist optional - Kunde kann ohne Kategorien starten

    setActionLoading(true);
    setError('');

    try {
      const now = new Date();
      let res: Response;

      if (nachtragModus) {
        // Nachtrag: Server bestimmt die Schicht aus dem Von-Zeitpunkt
        const tag = nachtragTag === 'heute'
          ? getTodayBerlin(now)
          : getTodayBerlin(new Date(now.getTime() - 24 * 60 * 60 * 1000));

        res = await fetch('/api/disruptions/nachtrag', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            tag,
            vonZeit: nachtragVon,
            bisZeit: nachtragBis,
            location: selectedLocation,
            machineId: selectedMachineId || undefined,
            categoryId: selectedCategoryId,
            stationId,
          }),
        });
      } else {
        const hour = now.getHours();
        let shiftType = 'EARLY';
        if (hour >= 14) shiftType = 'LATE';
        if (now.getDay() === 6) shiftType = 'SATURDAY';

        // Canonical Berlin-Tag (YYYY-MM-DD) - nicht client-lokal
        const shiftDateStr = getTodayBerlin(now);

        res = await fetch('/api/disruptions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            shiftDate: shiftDateStr,
            shiftType,
            location: selectedLocation,
            machineId: selectedMachineId || undefined,
            categoryId: selectedCategoryId,
            stationId,
          }),
        });
      }

      if (res.ok) {
        // Modal sofort schliessen, keine Erfolgs-Nachricht
        schliesseStartDialog();
        await loadActiveDisruptions();
      } else {
        const data = await res.json();
        setError(data.error || 'Fehler beim Starten');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!disruptionToResolve) return;

    setActionLoading(true);
    setError('');

    try {
      const res = await fetch(`/api/disruptions/${disruptionToResolve.id}/end`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (res.ok) {
        setShowResolveDialog(false);
        setDisruptionToResolve(null);
        await loadActiveDisruptions();
      } else {
        const data = await res.json();
        setError(data.error || 'Fehler beim Beenden');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDuration = (startedAt: string) => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin}m`;
    const hours = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center text-sm text-slate-500">
        Lade Maschinenstörungen...
      </div>
    );
  }

  // Sortiere Stoerungen: aelteste zuerst
  const sortedDisruptions = [...activeDisruptions].sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime()
  );

  const hasActive = sortedDisruptions.length > 0;

  return (
    <>
      {/* Störungen als Liste - kein umschließender roter Container */}
      <div className="rounded-lg border border-slate-200 bg-white">
        {/* Header - kein Titel (Abschnitt hat bereits "Störungen"), nur Badge + Action */}
        <div className="px-3 py-2 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            {hasActive ? (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-red-500 text-white rounded">
                {activeDisruptions.length} aktiv
              </span>
            ) : (
              <span className="text-xs text-slate-400">Keine aktiven — Maschine gestört? Hier melden.</span>
            )}
          </div>
          <button
            onClick={() => setShowStartDialog(true)}
            disabled={actionLoading}
            className="px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Störung melden
          </button>
        </div>

        {error && <div className="px-3 py-2 text-red-600 text-sm">{error}</div>}

        {/* Störungsliste - jedes Item einzeln mit Akzent */}
        <div>
          {hasActive && sortedDisruptions.map((disruption, idx) => (
            <div 
              key={disruption.id} 
              className="flex items-center justify-between gap-3"
              style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.06)',
                borderLeftWidth: '3px',
                borderLeftColor: '#ef4444',
                borderTop: idx > 0 ? '1px solid #e2e8f0' : 'none',
                padding: '8px 12px'
              }}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {(disruption.categoryNameSnapshot || disruption.category?.name) && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-slate-200 text-slate-700 rounded">
                      {disruption.categoryNameSnapshot || disruption.category?.name}
                    </span>
                  )}
                  <span className="text-sm font-medium text-slate-800 truncate">
                    {disruption.machine?.name || disruption.location || 'Unbekannt'}
                  </span>
                </div>
                <div className="text-xs text-slate-500">
                  seit {formatDuration(disruption.startedAt)}
                  {disruption.recordedBy && (
                    <span> · {disruption.recordedBy.displayName}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => { setDisruptionToResolve(disruption); setShowResolveDialog(true); }}
                disabled={actionLoading}
                className="px-2.5 py-1 text-xs font-medium bg-green-500 text-white hover:bg-green-600 rounded flex-shrink-0"
              >
                Behoben
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Start Dialog - Modal Overlay */}
      {showStartDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Maschinenstörung melden
                <ModuleInlineBadge moduleKey="disruptions" />
              </h3>
              <button
                onClick={schliesseStartDialog}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Läuft gerade vs. Nachtragen (Meldung #19) */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setNachtragModus(false); setError(''); }}
                  className={`p-2 text-sm rounded border ${
                    !nachtragModus
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white border-slate-300 hover:border-slate-400'
                  }`}
                >
                  Läuft gerade
                </button>
                <button
                  onClick={() => { setNachtragModus(true); setError(''); }}
                  className={`p-2 text-sm rounded border ${
                    nachtragModus
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white border-slate-300 hover:border-slate-400'
                  }`}
                >
                  Nachtragen (von–bis)
                </button>
              </div>

              {nachtragModus && (
                <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Tag der Störung
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {(['heute', 'gestern'] as const).map((tag) => (
                        <button
                          key={tag}
                          onClick={() => setNachtragTag(tag)}
                          className={`p-2 text-sm rounded border ${
                            nachtragTag === tag
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white border-slate-300 hover:border-slate-400'
                          }`}
                        >
                          {tag === 'heute' ? 'Heute' : 'Gestern'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Von
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="time"
                        value={nachtragVon}
                        onChange={(e) => setNachtragVon(e.target.value)}
                        className="w-full h-11 px-3 border border-slate-300 rounded-md text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Bis
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="time"
                        value={nachtragBis}
                        onChange={(e) => setNachtragBis(e.target.value)}
                        className="w-full h-11 px-3 border border-slate-300 rounded-md text-base"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Die Störung wird der Schicht zugeordnet, in der sie begonnen hat.
                  </p>
                </div>
              )}

              {/* Kategorie-Auswahl */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Kategorie
                  <span className="text-red-500 ml-1">*</span>
                </label>
                {categories.length === 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                    Keine Kategorien konfiguriert. Bitte im Admin-Bereich unter Konfiguration anlegen.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategoryId(cat.id)}
                        className={`p-2 text-xs text-center rounded border ${
                          selectedCategoryId === cat.id
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white border-slate-300 hover:border-slate-400'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Ort/Maschine-Auswahl */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  {selectedParentLine
                    ? `${selectedParentLine.name} - Variante wählen`
                    : stationType === 'WSA' ? 'Welche WSA-Maschine?' : 'Welche Strecke?'}
                  <span className="text-red-500 ml-1">*</span>
                </label>

              {/* Zurück-Button bei Sub-Strecken */}
              {selectedParentLine && (
                <button
                  onClick={() => setSelectedParentLine(null)}
                  className="mb-3 text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                >
                  ← Zurück zur Übersicht
                </button>
              )}

              {/* Sub-Strecken */}
              {selectedParentLine?.subLines && (
                <div className="grid grid-cols-2 gap-2">
                  {selectedParentLine.subLines.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setSelectedLocation(`Strecke: ${selectedParentLine.name} → ${sub.name}`)}
                      className={`p-2 text-sm text-left rounded border ${
                        selectedLocation === `Strecke: ${selectedParentLine.name} → ${sub.name}`
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      {sub.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Hauptansicht */}
              {!selectedParentLine && (
                <>
                  {/* Strecken für Vorarbeiter */}
                  {lines.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs text-slate-500 mb-2">Strecken</div>
                      <div className="grid grid-cols-2 gap-2">
                        {lines.map((line) => (
                          <button
                            key={line.id}
                            onClick={() => {
                              if (line.subLines && line.subLines.length > 0) {
                                setSelectedParentLine(line);
                              } else {
                                setSelectedLocation(`Strecke: ${line.name}`);
                              }
                            }}
                            className={`p-2 text-sm text-left rounded border flex items-center justify-between ${
                              selectedLocation === `Strecke: ${line.name}`
                                ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white border-slate-300 hover:border-slate-400'
                            }`}
                          >
                            {line.name}
                            {line.subLines && line.subLines.length > 0 && (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* WSA Maschinen */}
                  {machines.length > 0 && (
                    <div>
                      <div className="text-xs text-slate-500 mb-2">WSA Maschinen</div>
                      <div className="grid grid-cols-2 gap-2">
                        {machines.map((machine) => {
                          const locationKey = `WSA: ${machine.name}`;
                          const isDisrupted = disruptedLocations.includes(locationKey);
                          return (
                            <button
                              key={machine.id}
                              onClick={() => {
                                if (!isDisrupted) {
                                  setSelectedLocation(locationKey);
                                  setSelectedMachineId(machine.id);
                                }
                              }}
                              disabled={isDisrupted}
                              className={`p-2 text-sm text-left rounded border ${
                                isDisrupted
                                  ? 'bg-red-50 border-red-200 text-red-400 cursor-not-allowed'
                                  : selectedLocation === locationKey
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white border-slate-300 hover:border-slate-400'
                              }`}
                            >
                              {machine.name}
                              {isDisrupted && <span className="block text-[10px]">bereits gestoert</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Fallback wenn keine Daten */}
                  {machines.length === 0 && lines.length === 0 && (
                    <p className="text-sm text-slate-500">Keine Locations verfügbar</p>
                  )}
                </>
              )}

              {error && <div className="text-red-600 text-sm">{error}</div>}
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={schliesseStartDialog}
                className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleStart}
                disabled={actionLoading || !selectedLocation || (nachtragModus && (!nachtragVon || !nachtragBis))}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading
                  ? 'Melden...'
                  : nachtragModus ? 'Störung nachtragen' : 'Maschinenstörung melden'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Dialog */}
      {showResolveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Als behoben markieren
                <ModuleInlineBadge moduleKey="disruptions" />
              </h3>
              <button
                onClick={() => setShowResolveDialog(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-slate-600">
                Störung{disruptionToResolve?.location ? ` an "${disruptionToResolve.location}"` : ''} als behoben markieren?
              </p>
              {disruptionToResolve && (
                <p className="text-xs text-slate-400 mt-2">
                  Laufzeit: {formatDuration(disruptionToResolve.startedAt)}
                </p>
              )}
              {error && <div className="text-red-600 text-sm mt-3">{error}</div>}
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowResolveDialog(false)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleResolve}
                disabled={actionLoading}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Speichern...' : 'Als behoben markieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
